import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Container } from "@/components/shared";
import { ArrowRight } from "lucide-react";

const PILLARS = [
  { word: "Mémoire", desc: "On n'oublie pas. On documente, on transmet." },
  { word: "Conscience", desc: "Comprendre le monde pour mieux y répondre." },
  { word: "Ironie", desc: "L'humour comme arme d'intelligence." },
  { word: "Histoire", desc: "Les faits d'hier éclairent les débats d'aujourd'hui." },
  { word: "Transmission", desc: "Ce qu'on apprend, on le partage." },
  { word: "Protection", desc: "Connaître ses droits, c'est se protéger." },
  { word: "Argumentation", desc: "Répondre avec des preuves, jamais avec des cris." },
  { word: "Intelligence", desc: "La meilleure réponse à la bêtise." },
  { word: "Calme", desc: "La sérénité désarme plus sûrement que la colère." },
];

const TRANSFORM = [
  ["les malaises", "des connaissances"],
  ["les attaques", "des arguments"],
  ["les émotions", "de la réflexion"],
];

export default function About() {
  const navigate = useNavigate();
  const ease = [0.22, 1, 0.36, 1];

  return (
    <Container className="max-w-4xl">
      {/* Manifesto */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }} className="mb-14">
        <p className="text-gold text-[10px] md:text-xs uppercase tracking-[0.3em] font-semibold mb-5">La maison BIZARRE</p>
        <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.98] font-light">
          Ce n'est pas une IA.<br /><span className="gold-text italic">C'est un univers.</span>
        </h1>
        <p className="text-white/60 text-base md:text-xl font-light mt-6 leading-relaxed max-w-2xl">
          BIZARRE AI n'est pas un assistant de plus. C'est une maison où chaque pièce vous aide à comprendre,
          à répondre et à agir — avec mémoire, calme et intelligence.
        </p>
      </motion.div>

      {/* Transformation credo */}
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease }}
        className="card-surface noise p-7 md:p-10 mb-14">
        <p className="text-white/45 text-[10px] uppercase tracking-[0.25em] mb-6">Notre philosophie</p>
        <div className="space-y-4">
          {TRANSFORM.map(([a, b], i) => (
            <p key={i} className="font-display text-xl md:text-3xl font-light leading-tight">
              On transforme <span className="text-white/50">{a}</span> en <span className="gold-text">{b}</span>.
            </p>
          ))}
        </div>
        <p className="text-white/45 text-sm mt-6">On ne répond jamais par la haine. On fait réfléchir.</p>
      </motion.div>

      {/* Pillars */}
      <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-6">Nos piliers</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
        {PILLARS.map((p, i) => (
          <motion.div key={p.word} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.05, ease }} className="card-surface p-5" data-testid={`pillar-${p.word}`}>
            <p className="font-display text-lg gold-text mb-1">{p.word}</p>
            <p className="text-white/55 text-sm leading-relaxed">{p.desc}</p>
          </motion.div>
        ))}
      </div>

      {/* Community door — subtle nod */}
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
        className="border-l-2 border-gold/40 pl-5 py-2 mb-16" data-testid="about-community-nod">
        <p className="text-white/40 text-[10px] uppercase tracking-[0.25em] mb-2">Là où tout a commencé</p>
        <p className="font-display text-lg md:text-xl font-light italic text-white/70 leading-relaxed">
          « Tu n'aimes pas les Noirs, petit raciste ? »
        </p>
        <p className="text-white/40 text-sm mt-3 max-w-xl leading-relaxed">
          Une phrase devenue une porte. Derrière elle, un univers bien plus vaste : la mémoire, l'histoire,
          l'esprit critique et la transmission. Bienvenue chez vous.
        </p>
      </motion.div>

      {/* CTA */}
      <div className="text-center pt-4 border-t border-white/10">
        <p className="font-display text-xl md:text-2xl font-light mb-5">
          Bienvenue dans le <span className="gold-text italic">bizarre.</span>
        </p>
        <button data-testid="about-cta" onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 gold-gradient text-black rounded-full px-7 py-3.5 font-semibold hover:opacity-90 transition-opacity">
          Explorer la maison <ArrowRight size={18} />
        </button>
      </div>
    </Container>
  );
}
